/**
 * GoalCalculator Component
 * WP-RETIRE-5 Change 4: "What Do I Need?" section below heatmap
 *
 * Shows required monthly savings for all 3 return rate scenarios
 * to achieve "Well Funded" status at a user-specified retirement age.
 */

import { cn } from '@/utils/cn';

interface GoalCalculatorProps {
  goalRetirementAge: number;
  onGoalRetirementAgeChange: (age: number) => void;
  currentAge: number;
  currentMonthlySavings: number; // dollars
  goalResults: {
    belowAverage: number;
    average: number;
    aboveAverage: number;
  } | null;
  /** Show built-in slider (for standalone use). When false, age is controlled externally. */
  showSlider?: boolean;
}

interface ScenarioResult {
  label: string;
  returnRate: string;
  requiredSavings: number; // dollars/mo or Infinity
}

export default function GoalCalculator({
  goalRetirementAge,
  onGoalRetirementAgeChange,
  currentAge,
  currentMonthlySavings,
  goalResults,
  showSlider = false,
}: GoalCalculatorProps) {
  const minAge = currentAge + 1;
  const maxAge = 90;

  // Build scenario list
  const scenarios: ScenarioResult[] = goalResults
    ? [
        { label: 'Above Average', returnRate: '5%', requiredSavings: goalResults.aboveAverage },
        { label: 'Average', returnRate: '4%', requiredSavings: goalResults.average },
        { label: 'Below Average', returnRate: '3%', requiredSavings: goalResults.belowAverage },
      ]
    : [];

  const allMet = scenarios.every(
    (s) => s.requiredSavings !== Infinity && currentMonthlySavings >= s.requiredSavings
  );

  return (
    <div className="mt-8 border-t border-gray-200 pt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        What Do I Need?
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Required monthly savings to be &ldquo;Well Funded&rdquo; at retirement age {goalRetirementAge}.
      </p>

      {/* Optional built-in slider for standalone use */}
      {showSlider && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="goal-retirement-age" className="text-sm font-medium text-gray-700">
              Target Retirement Age
            </label>
            <span className="text-lg font-bold text-gray-900">{goalRetirementAge}</span>
          </div>
          <input
            id="goal-retirement-age"
            type="range"
            min={minAge}
            max={maxAge}
            value={goalRetirementAge}
            onChange={(e) => onGoalRetirementAgeChange(parseInt(e.target.value, 10))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{minAge}</span>
            <span>{maxAge}</span>
          </div>
        </div>
      )}

      {/* Results */}
      {goalResults === null ? (
        <p className="text-sm text-gray-500 italic">
          Adjust your age and target retirement age above to see results.
        </p>
      ) : (
        <>
          {allMet && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
              <span className="text-green-800 font-medium text-sm">
                You&apos;re on track! Your current savings rate meets all scenarios.
              </span>
            </div>
          )}

          <div className="space-y-3">
            {scenarios.map((scenario) => {
              const isMet =
                scenario.requiredSavings !== Infinity &&
                currentMonthlySavings >= scenario.requiredSavings;
              const isImpossible = scenario.requiredSavings === Infinity;

              return (
                <div
                  key={scenario.label}
                  className={cn(
                    'flex items-center justify-between p-3 rounded-lg border',
                    isMet
                      ? 'bg-green-50 border-green-200'
                      : isImpossible
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-white border-gray-200'
                  )}
                >
                  <div className="flex items-center gap-2">
                    {isMet ? (
                      <svg
                        className="w-5 h-5 text-green-600 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      <div className="w-5 h-5 flex-shrink-0" />
                    )}
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {scenario.label}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">
                        ({scenario.returnRate} return)
                      </span>
                    </div>
                  </div>

                  <div className="text-right">
                    {isImpossible ? (
                      <span className="text-sm text-gray-500 italic">
                        Not achievable with savings alone
                      </span>
                    ) : scenario.requiredSavings === 0 ? (
                      <span className="text-sm font-medium text-green-700">
                        Already funded
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'text-sm font-semibold',
                          isMet ? 'text-green-700' : 'text-gray-900'
                        )}
                      >
                        ${scenario.requiredSavings.toLocaleString()}/mo
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
